import type { ServiceOptionsAndVariants } from '@wix/ambassador-bookings-catalog-v1-service-options-and-variants/types';
import {
  setZeroMSToIsoDate,
  convertRfcTimeToLocalDateTimeStartOfDay,
  getTodayLocalDateTimeStartOfDay,
  getNowAsISOStringInTimezone,
} from '../utils/dateAndTime/dateAndTime';
import { createDummySlots } from './dummyData/dummySlotsData';
import { BookingsApi } from './BookingsApi';
import { CalendarApiInitParams } from './types';
import {
  CalendarErrors,
  LocalDateTimeRange,
  Optional,
  Preset,
  Preference,
} from '../types/types';
import {
  areAllLocationsSelected,
  filterServicesBySelectedLocations,
  filterSlotsBySelectedLocations,
  isOnlyBusinessLocationsSelected,
  isOtherLocationsSelected,
} from '../utils/selectedLocations/selectedLocations';
import { EmptyStateType } from '../components/BookingCalendar/ViewModel/emptyStateViewModel/emptyStateViewModel';
import { CalendarState } from '../components/BookingCalendar/controller';
import {
  ControllerFlowAPI,
  ControllerParams,
  IUser,
} from '@wix/yoshi-flow-editor';
import { AddError } from '../components/BookingCalendar/Actions/addError/addError';
import { createDummyDateAvailability } from './dummyData/dummyDateAvailability';
import { Balance } from '@wix/ambassador-pricing-plan-benefits-server/types';
import { Booking } from '@wix/bookings-checkout-api';
import {
  isCalendarPage,
  isCalendarWidget,
  isDailyAgendaWidget,
  isWeeklyTimeTableWidget,
} from '../utils/presets';
import { isLayoutWithTimeSlot } from '../utils/layouts';
import { CalendarContext } from '../utils/context/contextFactory';
import {
  BookingsQueryParams as BookingsQueryParamsUtils,
  CalendarSelections,
  getInstance,
  getServerBaseUrl,
  getServiceSlug,
  getUrlQueryParamValue,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import {
  LocationType as ServiceLocationType,
  Service,
  ServiceType,
} from '@wix/ambassador-bookings-services-v2-service/types';
import {
  isServiceConnectedToPricingPlan,
  isServiceWithVariedPrice,
  isBookFlowSettingsEnabled,
} from '@wix/bookings-calendar-catalog-viewer-mapper';
import {
  createDummyServices,
  isDummyServices,
} from './dummyData/dummyServices';
import {
  ListAvailabilityTimeSlotsResponse,
  TimeSlot,
  LocationType,
} from '@wix/ambassador-bookings-availability-v2-time-slot/types';
import {
  LocationLocationType,
  V2Slot,
  SelectionMethod,
} from '@wix/ambassador-bookings-v2-booking/types';
import { fetchAvailability } from './fetchAvailability';
import { isAppointmentBooking, isClassBooking } from '../utils/booking/booking';
import { getStaffList } from '../utils/slotAvailability/slotAvailability';
import {
  BookingsSettings,
  Features,
  GetBookingsSettingsResponse,
  SiteProperties,
  Strategy,
} from '@wix/ambassador-bookings-v2-bookings-settings/types';
import { SelectedBookingPreference } from '../utils/bookingPreferences/bookingPreferences';
import { DO_NOT_CARE_STAFF, STAFF_RESOURCE_ID } from '../constants/constants';

export const CALENDAR_PAGE_URL_PATH_PARAM = 'booking-calendar';

export class CalendarApi {
  private readonly flowAPI: ControllerFlowAPI;
  private bookingsApi: BookingsApi;
  private readonly reportError: ControllerParams['flowAPI']['reportError'];
  private readonly settingsParams: any;
  private readonly preset: Preset;
  constructor({
    flowAPI,
    reportError,
    settingsParams,
    preset,
  }: CalendarApiInitParams) {
    this.flowAPI = flowAPI;
    this.settingsParams = settingsParams;

    this.reportError = reportError;
    this.preset = preset;
    this.bookingsApi = new BookingsApi({
      getAuthorization: () =>
        getInstance({
          wixCodeApi: flowAPI.controllerConfig.wixCodeApi,
          appParams: flowAPI.controllerConfig.appParams,
        }),
      baseUrl: getServerBaseUrl({
        wixCodeApi: flowAPI.controllerConfig.wixCodeApi,
        appParams: flowAPI.controllerConfig.appParams,
      }),
      experiments: flowAPI.experiments,
      httpClient: flowAPI.httpClient,
    });
  }

  async getBusinessInfo(): Promise<GetBookingsSettingsResponse> {
    let response;
    try {
      response = await this.bookingsApi.getBusinessInfo();
    } catch (e) {
      const authorization = getInstance({
        wixCodeApi: this.flowAPI.controllerConfig.wixCodeApi,
        appParams: this.flowAPI.controllerConfig.appParams,
      });

      this.reportError(
        `failed to get businessInfo from BusinessCatalog with error: ${e} with authorization ${authorization}` as
          | string
          | Error,
      );
      const features = {
        bookingsCourses: { enabled: true },
        bookingsGroups: { enabled: true },
        bookingsAppointments: { enabled: true },
        bookingsPayments: { enabled: true },
        bookingsReminders: { enabled: true },
        bookingsSmsReminders: { enabled: true },
      } as Features;
      const siteProperties = {
        name: '',
        language: 'en',
        timeZone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
        locale: 'en',
      } as SiteProperties;
      response = {
        bookingsSettings: {
          features,
          siteProperties,
        } as BookingsSettings,
      } as GetBookingsSettingsResponse;
    }
    return response;
  }

  async queryServices({
    onError,
    calendarSelections,
    isDateAndTimeViewMode,
  }: {
    onError: (type: EmptyStateType) => void;
    calendarSelections?: CalendarSelections;
    isDateAndTimeViewMode?: boolean;
  }): Promise<
    Optional<{
      services: Service[];
      servicesVariants?: ServiceOptionsAndVariants[];
    }>
  > {
    const serviceIds = calendarSelections?.services?.map(
      (service) => service.id,
    );

    const shouldReturnEmptyResponse = isDateAndTimeViewMode && !serviceIds;
    const isDateAndTimeViewModeInSSR =
      isDateAndTimeViewMode && this.flowAPI.environment.isSSR;
    if (shouldReturnEmptyResponse) {
      !isDateAndTimeViewModeInSSR && onError(EmptyStateType.SERVICE_NOT_FOUND);
      return { services: [] };
    }

    try {
      const isEditorMode = this.flowAPI.environment.isEditor;
      const isCalendarPageInEditorMode =
        isCalendarPage(this.preset) && isEditorMode;
      const isCalendar =
        isCalendarPage(this.preset) || isCalendarWidget(this.preset);

      const selectedService = this.flowAPI.settings.get(
        this.settingsParams.selectedService,
      );

      if (
        isCalendarWidget(this.preset) &&
        (!selectedService || selectedService === '')
      ) {
        onError(EmptyStateType.WIDGET_NO_SELECTED_SERVICE);
        return { services: [] };
      }

      const serviceSlug = await getServiceSlug({
        wixCodeApi: this.flowAPI.controllerConfig.wixCodeApi,
        pageName: CALENDAR_PAGE_URL_PATH_PARAM,
      });

      const resourceSlug = this.getResourceSlug();
      const selectedLocations = this.flowAPI.settings.get(
        this.settingsParams.selectedLocations,
      );
      const selectedCategories = this.flowAPI.settings.get(
        this.settingsParams.selectedCategories,
      );

      const serviceIdsFilter = isCalendarWidget(this.preset)
        ? [selectedService]
        : serviceIds;
      const shouldFilterBySlug =
        isCalendarPage(this.preset) && !isDateAndTimeViewMode;

      const includeServices =
        !isCalendarPageInEditorMode && !isDateAndTimeViewModeInSSR;

      const queryServicesOptions = {
        ...(shouldFilterBySlug ? { slug: serviceSlug } : {}),
        businessLocationIds:
          !isCalendarPageInEditorMode &&
          this.shouldFilterBySelectedLocationsOnServerSide()
            ? selectedLocations
            : undefined,
        categories:
          selectedCategories?.length > 0 ? selectedCategories : undefined,
        ids: serviceIdsFilter,
        types: isCalendar
          ? [ServiceType.CLASS, ServiceType.APPOINTMENT]
          : [ServiceType.CLASS],
        isBookOnlineAllowed: isCalendar ? true : undefined,
      };

      let services: Service[];

      if (includeServices) {
        const queryServicesResponse = await this.bookingsApi.queryServices(
          queryServicesOptions,
        );

        services = queryServicesResponse.services || [];
      } else {
        services = [];
      }

      if (isEditorMode) {
        const shouldUseDummyData = () => {
          if (isCalendarPage(this.preset)) {
            return true;
          }
          if (isCalendarWidget(this.preset)) {
            return false;
          }
          if (
            isWeeklyTimeTableWidget(this.preset) ||
            isDailyAgendaWidget(this.preset)
          ) {
            return !services.length;
          }
          return true;
        };
        const useDummyData = shouldUseDummyData();
        if (useDummyData) {
          return {
            services: createDummyServices(this.flowAPI),
          };
        }
      }

      if (
        !serviceIds &&
        (isCalendarPage(this.preset) || isCalendarWidget(this.preset))
      ) {
        services = this.populateRequestedServiceAndAllServices(
          services,
          serviceSlug,
        );
      }

      if (this.shouldFilterBySelectedLocationsOnClientSide()) {
        services = filterServicesBySelectedLocations(
          selectedLocations,
          services,
        );
      }

      if (!services?.[0]) {
        !isDateAndTimeViewModeInSSR &&
          onError(EmptyStateType.SERVICE_NOT_FOUND);
        return { services: [] };
      }

      const servicesIdsWithVariants = services
        .filter(isServiceWithVariedPrice)
        .map((service) => service.id!);
      let servicesVariants;
      if (
        isLayoutWithTimeSlot(this.flowAPI.settings, this.settingsParams) &&
        servicesIdsWithVariants.length
      ) {
        servicesVariants = await this.bookingsApi.getServiceVariants(
          servicesIdsWithVariants,
        );
      }

      return {
        services,
        servicesVariants,
      };
    } catch (e) {
      const authorization = getInstance({
        wixCodeApi: this.flowAPI.controllerConfig.wixCodeApi,
        appParams: this.flowAPI.controllerConfig.appParams,
      });

      this.reportError(
        `failed to get catalog data from catalog bulk with error: ${e} with authorization ${authorization}` as
          | string
          | Error,
      );
      onError(EmptyStateType.SERVER_ERROR);
    }
  }

  async getNextAvailableDate(
    { fromAsLocalDateTime, toAsLocalDateTime }: LocalDateTimeRange,
    {
      state,
      context,
      onError,
    }: {
      state: CalendarState;
      context: CalendarContext;
      onError: AddError;
    },
  ): Promise<Optional<string>> {
    try {
      const slotAvailability = await fetchAvailability({
        from: fromAsLocalDateTime,
        to: toAsLocalDateTime,
        state,
        context,
        getNextAvailableSlot: true,
        flowAPI: this.flowAPI,
        reportError: this.reportError,
        settingsParams: this.settingsParams,
      });

      const nextAvailableDate =
        slotAvailability?.timeSlots?.[0]?.localStartDate;
      if (nextAvailableDate) {
        return convertRfcTimeToLocalDateTimeStartOfDay(nextAvailableDate!);
      }
      onError(CalendarErrors.NO_NEXT_AVAILABLE_DATE_WARNING);
    } catch (e) {
      this.reportError(e as string | Error);
      onError(CalendarErrors.NEXT_AVAILABLE_DATE_SERVER_ERROR);
    }
  }

  async getDateAvailability(
    { fromAsLocalDateTime, toAsLocalDateTime }: LocalDateTimeRange,
    {
      state,
      context,
    }: {
      context: CalendarContext;
      state: CalendarState;
    },
  ): Promise<Optional<ListAvailabilityTimeSlotsResponse>> {
    if (this.flowAPI.environment.isEditor) {
      return {
        timeSlots: createDummyDateAvailability().availabilityEntries,
      };
    }

    try {
      let from;
      const { selectedTimezone } = state;
      const todayLocalDateTime = getTodayLocalDateTimeStartOfDay(
        selectedTimezone!,
      );
      if (new Date(toAsLocalDateTime) < new Date(todayLocalDateTime)) {
        return {};
      } else {
        from =
          new Date(todayLocalDateTime) > new Date(fromAsLocalDateTime)
            ? todayLocalDateTime
            : fromAsLocalDateTime;
      }
      const slotAvailability = await fetchAvailability({
        from,
        to: toAsLocalDateTime,
        state,
        context,
        shouldLimitPerDay: true,
        flowAPI: this.flowAPI,
        reportError: this.reportError,
        settingsParams: this.settingsParams,
      });

      return slotAvailability;
    } catch (e) {
      this.reportError(e as string | Error);
    }
  }

  async getSlotsInRange(
    { fromAsLocalDateTime, toAsLocalDateTime }: LocalDateTimeRange,
    {
      state,
      context,
      onError,
    }: {
      state: CalendarState;
      context: CalendarContext;
      onError: AddError;
    },
  ): Promise<Optional<ListAvailabilityTimeSlotsResponse>> {
    const shouldCreateDummySlots =
      this.flowAPI.environment.isEditor &&
      isDummyServices(state.servicesInView);
    if (shouldCreateDummySlots) {
      return createDummySlots({
        flowAPI: this.flowAPI,
        settingsParams: this.settingsParams,
        from: fromAsLocalDateTime,
      });
    }

    try {
      const slotAvailability = await fetchAvailability({
        from: fromAsLocalDateTime,
        to: toAsLocalDateTime,
        state,
        context,
        flowAPI: this.flowAPI,
        reportError: this.reportError,
        settingsParams: this.settingsParams,
      });

      if (isLayoutWithTimeSlot(this.flowAPI.settings, this.settingsParams)) {
        slotAvailability!.timeSlots = this.getOnlyFutureSlotAvailabilities(
          slotAvailability?.timeSlots,
          state.selectedTimezone!,
        );
      }

      if (this.shouldFilterBySelectedLocationsOnClientSide()) {
        const selectedLocations = this.flowAPI.settings.get(
          this.settingsParams.selectedLocations,
        );
        slotAvailability!.timeSlots = filterSlotsBySelectedLocations(
          selectedLocations,
          slotAvailability?.timeSlots,
        );
      }

      return {
        timeSlots: slotAvailability!.timeSlots,
      };
    } catch (e) {
      this.reportError(e as string | Error);
      onError(CalendarErrors.AVAILABLE_SLOTS_SERVER_ERROR);
    }
  }

  async getBookingDetails({
    onError,
  }: {
    onError: (type: EmptyStateType) => void;
  }): Promise<Optional<Booking>> {
    const bookingId = getUrlQueryParamValue(
      this.flowAPI.controllerConfig.wixCodeApi,
      BookingsQueryParamsUtils.BOOKING_ID,
    );

    if (!bookingId || this.flowAPI.environment.isSSR) {
      return;
    }
    try {
      const booking = await this.bookingsApi.getBookingDetails(bookingId);
      if (!booking) {
        onError(EmptyStateType.BOOKING_NOT_FOUND);
        return;
      }
      return booking;
    } catch (e: any) {
      this.reportError(e as string | Error);
      const errorType =
        e?.httpStatus === 403
          ? EmptyStateType.GET_BOOKING_DETAILS_ACCESS_DENIED
          : EmptyStateType.GET_BOOKING_DETAILS_ERROR;
      onError(errorType);
    }
  }

  async rescheduleBooking({
    booking,
    slot,
    timezone,
    onError,
    bookingsSettings,
    selectedBookingPreferences,
  }: {
    booking: Booking;
    slot: TimeSlot;
    timezone: string;
    onError: AddError;
    bookingsSettings?: BookingsSettings;
    selectedBookingPreferences?: SelectedBookingPreference[];
  }) {
    try {
      let slotToUse: V2Slot;
      if (isAppointmentBooking(booking)) {
        slotToUse = {
          scheduleId: slot.scheduleId!,
          serviceId: slot.serviceId!,
          startDate: setZeroMSToIsoDate(slot.localStartDate!),
          endDate: setZeroMSToIsoDate(slot.localEndDate!),
          timezone,
          location: {
            id: slot.location?.id,
            name: slot.location?.name,
            locationType: mapLocationType(slot.location?.locationType),
          },
        };

        const isBookFlowEnabled = isBookFlowSettingsEnabled({
          experiments: this.flowAPI.experiments,
          bookingsSettings,
        });

        if (isBookFlowEnabled) {
          const staffSelectionStrategy =
            bookingsSettings?.staffSelection?.strategy;

          if (staffSelectionStrategy === Strategy.CUSTOMER_MAY_CHOOSE) {
            const staffPreference = selectedBookingPreferences?.find(
              (pref) => pref.key === Preference.STAFF_MEMBER,
            );
            const selectedStaffId = staffPreference?.value;

            if (selectedStaffId === DO_NOT_CARE_STAFF) {
              slotToUse.resourceSelections = [
                {
                  resourceTypeId: STAFF_RESOURCE_ID,
                  selectionMethod: SelectionMethod.ANY_RESOURCE,
                },
              ];
            } else {
              slotToUse.resource = getStaffList(slot)[0]!;
            }
          } else {
            slotToUse.resource = getStaffList(slot)[0]!;
          }
        } else {
          slotToUse.resource = getStaffList(slot)[0]!;
        }
      } else if (isClassBooking(booking)) {
        slotToUse = {
          eventId: slot.eventInfo?.eventId,
        };
      } else {
        throw new Error('Invalid booking type');
      }

      return await this.bookingsApi.rescheduleBooking({
        booking,
        slot: slotToUse,
      });
    } catch (e) {
      this.reportError(e as string | Error);
      onError(CalendarErrors.RESCHEDULE_SERVER_ERROR);
    }
  }

  async getPurchasedPricingPlans({
    currentUser,
    service,
  }: {
    currentUser: IUser;
    service?: Service;
  }): Promise<Balance[]> {
    const shouldGetPurchasedPricingPlans =
      currentUser?.loggedIn &&
      service &&
      isServiceConnectedToPricingPlan(service);
    try {
      const contactId = currentUser.id;
      if (
        this.flowAPI.environment.isEditor ||
        !shouldGetPurchasedPricingPlans
      ) {
        return [];
      }
      return await this.bookingsApi.getPurchasedPricingPlans({
        contactId,
        authorization: getInstance({
          wixCodeApi: this.flowAPI.controllerConfig.wixCodeApi,
          appParams: this.flowAPI.controllerConfig.appParams,
        }),
      });
    } catch (e) {
      this.reportError(e as string | Error);
      return [];
    }
  }

  private getOnlyFutureSlotAvailabilities(
    availableSlots: TimeSlot[] | undefined,
    selectedTimezone: string,
  ): TimeSlot[] {
    const now = getNowAsISOStringInTimezone(selectedTimezone);
    const onlyFutureEntries = availableSlots?.filter((availabilityEntry) => {
      return (
        availabilityEntry?.localStartDate &&
        availabilityEntry?.localStartDate >= now
      );
    });
    return onlyFutureEntries || [];
  }

  private populateRequestedServiceAndAllServices(
    services: Service[],
    serviceSlug: string,
  ) {
    if (!serviceSlug) {
      return services.slice(0, 1);
    }
    return services;
  }

  private shouldFilterBySelectedLocationsOnClientSide() {
    const selectedLocations = this.flowAPI.settings.get(
      this.settingsParams.selectedLocations,
    );
    return (
      !isLayoutWithTimeSlot(this.flowAPI.settings, this.settingsParams) &&
      !areAllLocationsSelected(selectedLocations) &&
      isOtherLocationsSelected(selectedLocations)
    );
  }

  private shouldFilterBySelectedLocationsOnServerSide() {
    return (
      !isLayoutWithTimeSlot(this.flowAPI.settings, this.settingsParams) &&
      isOnlyBusinessLocationsSelected(
        this.flowAPI.settings.get(this.settingsParams.selectedLocations),
      )
    );
  }

  private getResourceSlug() {
    const staffQueryParam = getUrlQueryParamValue(
      this.flowAPI.controllerConfig.wixCodeApi,
      BookingsQueryParamsUtils.STAFF,
    );
    if (staffQueryParam) {
      if (Array.isArray(staffQueryParam)) {
        return staffQueryParam[0];
      } else {
        return staffQueryParam;
      }
    }
  }

  queryServicesV2({ serviceIds }: { serviceIds: string[] }) {
    return this.bookingsApi.queryServices({
      ids: serviceIds,
      isBookOnlineAllowed: true,
    });
  }
}

const mapLocationType = (
  locationType: LocationType | undefined,
): LocationLocationType | undefined => {
  if (!locationType) {
    return undefined;
  }

  const locationTypeMap: Record<LocationType, LocationLocationType> = {
    [ServiceLocationType.CUSTOMER]: LocationLocationType.CUSTOM,
    [ServiceLocationType.BUSINESS]: LocationLocationType.OWNER_BUSINESS,
    [ServiceLocationType.CUSTOM]: LocationLocationType.OWNER_CUSTOM,
    [ServiceLocationType.UNKNOWN_LOCATION_TYPE]: LocationLocationType.UNDEFINED,
  };

  return locationTypeMap[locationType];
};
